import { useEffect, useRef, useState } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import IconButton from "@mui/material/IconButton"
import Avatar from "@mui/material/Avatar"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { MONO, CHAT_COLOR, AGENT_COLORS } from "../constants"
import { scrollbarSx } from "../../../shared/scrollbar"

function SearchResultRow({ msg, profiles }) {
  const sender = msg.username ?? msg.from ?? "?"
  const isAgent = sender === "jarvis" || sender === "friday"
  const profile = profiles?.[sender]
  const agentColor = AGENT_COLORS[sender] ?? null
  const senderColor = profile?.name_color ?? agentColor ?? CHAT_COLOR
  const displayName = profile?.display_name ?? (isAgent ? sender.toUpperCase() : sender)
  const avatarSrc = profile?.avatar_b64 || undefined
  const ts = new Date((msg.timestamp ?? 0) * 1000).toLocaleString([], {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  })
  return (
    <Box sx={{ display: "flex", gap: 0.75, px: 1, py: 0.75, borderRadius: 1,
      backgroundColor: "rgba(var(--app-fg-rgb),0.03)", border: "1px solid rgba(var(--app-fg-rgb),0.05)", mb: 0.5 }}>
      {avatarSrc
        ? <Avatar src={avatarSrc} sx={{ width: 22, height: 22, flexShrink: 0 }} />
        : <Avatar sx={{ width: 22, height: 22, flexShrink: 0, bgcolor: senderColor + "22",
            border: "1px solid " + senderColor + "44", color: senderColor, fontSize: 10, fontFamily: MONO }}>
            {displayName[0]?.toUpperCase()}
          </Avatar>}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
          <Typography sx={{ fontFamily: MONO, fontSize: "0.65rem", color: senderColor, fontWeight: 600 }}>
            {displayName}
          </Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: "0.55rem", color: "rgba(var(--app-fg-rgb),0.28)" }}>
            {ts}
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: MONO, fontSize: "0.68rem", color: "rgba(var(--app-fg-rgb),0.75)",
          wordBreak: "break-word", lineHeight: 1.5 }}>
          {msg.text || (msg.image ? "[image]" : msg.voice ? "[voice]" : "")}
        </Typography>
      </Box>
    </Box>
  )
}

export function SearchPanel({ channel, viewer, other, groupId, profiles, onClose }) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(debounceRef.current)
    const q = query.trim()
    if (!q) { setResults([]); setLoading(false); return }
    setLoading(true)
    debounceRef.current = setTimeout(() => {
      const params = new URLSearchParams({ q, channel })
      if (channel === "private") { params.set("viewer", viewer); params.set("with", other) }
      if (channel === "group") params.set("group_id", groupId)
      fetch("/api/messenger/search/?" + params.toString())
        .then(r => r.json())
        .then(d => setResults(d.results ?? []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false))
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [query, channel, viewer, other, groupId])

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.7,
        borderBottom: "1px solid rgba(var(--app-fg-rgb),0.06)", flexShrink: 0 }}>
        <IconButton size="small" onClick={onClose} sx={{ p: 0, color: "rgba(var(--app-fg-rgb),0.5)" }}>
          <ArrowBackIcon sx={{ fontSize: 15 }} />
        </IconButton>
        <TextField autoFocus fullWidth size="small" value={query}
          onChange={(e) => setQuery(e.target.value)} placeholder="Search this chat…"
          sx={{
            "& .MuiInputBase-root": { backgroundColor: "transparent", p: "2px 8px" },
            "& .MuiInputBase-input": { fontFamily: MONO, fontSize: "0.72rem", color: "rgba(var(--app-fg-rgb),0.88)", p: "4px 0" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "rgba(var(--app-fg-rgb),0.07)" },
              "&:hover fieldset": { borderColor: "rgba(245,158,11,0.22)" },
              "&.Mui-focused fieldset": { borderColor: "rgba(245,158,11,0.42)" },
            },
          }} />
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", px: 1.25, py: 1, ...scrollbarSx }}>
        {loading && (
          <Typography sx={{ color: "rgba(var(--app-fg-rgb),0.2)", fontFamily: MONO, fontSize: "0.65rem", textAlign: "center" }}>
            Searching…
          </Typography>
        )}
        {!loading && query.trim() && results.length === 0 && (
          <Typography sx={{ color: "rgba(var(--app-fg-rgb),0.17)", fontFamily: MONO, fontSize: "0.65rem", textAlign: "center" }}>
            No matches.
          </Typography>
        )}
        {!loading && !query.trim() && (
          <Typography sx={{ color: "rgba(var(--app-fg-rgb),0.17)", fontFamily: MONO, fontSize: "0.65rem", textAlign: "center" }}>
            Type to search messages in this chat.
          </Typography>
        )}
        {results.map(msg => <SearchResultRow key={msg.id} msg={msg} profiles={profiles} />)}
      </Box>
    </Box>
  )
}
